import React, { useEffect, useCallback, useState } from 'react';
import { Select, Row, Table, Tooltip, Button } from 'antd';
import Navbar from 'src/common/Navbar/Navbar';
import {
  MoveableAssetsProps,
  MoveableAssetsRecord,
} from './MoveableAssets.utils';
import { ReloadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import { getMoveableAssets } from './MoveableAssets.actionsCreator';
import { formatDateTime } from 'src/utils/utils';
import { MoveableAssetsStyles } from './MoveableAssets.styles';
import SimplePaginator from 'src/common/SimplePaginator';
import { SET_MOVEABLE_ASSETS_PAGINATION } from 'src/common/actions/constants';
import AddMoveableAssetModal from './AddMoveableAssetModal';

const MoveableAssets: React.FC<MoveableAssetsProps> = (
  props: MoveableAssetsProps,
) => {
  const {
    classes,
    moveableAssets,
    getMoveableAssets,
    setPagination,
    columnList,
    vendorConfig,
    t,
  } = props;

  const { pagination } = moveableAssets;
  const allow_moveable_asset_creation =
    vendorConfig?.transportation_unit_config?.allow_transportation_unit_creation !== false;

  const [state, setState] = useState({
    isMoveableAssetModalVisible: false,
  });

  const fetchViewDetailsAndCount = () => {
    getMoveableAssets({ ...pagination });
  };

  useEffect(() => {
    fetchViewDetailsAndCount();
  }, [pagination]);

  const handleCloseModal = (modalName: string) => {
    setState((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
  };

  const showModals = useCallback(
    (modalName: string) => {
      setState((prevState) => ({
        ...prevState,
        [modalName]: true,
      }));
    },
    [],
  );

  const generateColumnsToShow = useCallback(() => {
    const columnData: {
      title: string;
      sorter: boolean;
      dataIndex: string;
      key: string;
      width: number;
      render?: (value: any, record: MoveableAssetsRecord) => JSX.Element | string;
    }[] = [];

    columnList?.forEach((item) => {
      const column_id = item.is_sortable ? item.sort_key : item.column_id;

      const itemToPush = {
        title: t(item.pretty_name),
        sorter: item.is_sortable,
        dataIndex: column_id,
        key: column_id,
        width: 190,
      };

      if (column_id === 'is_active') {
        itemToPush['render'] = (_, record) => (
          record.is_active ? <CheckOutlined /> : <CloseOutlined />
        );
      }

      columnData.push(itemToPush);
    });

    return columnData;
  }, [columnList, t]);

  const renderFilters = () => {
    return (
      <Row
        style={{
          background: '#fff',
          padding: '12px 16px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1 }}>
          {allow_moveable_asset_creation && (
            <Button
              onClick={() => showModals('isMoveableAssetModalVisible')}
            >
              {t('Add New Moveable Asset')}
            </Button>
          )}
          <SimplePaginator
            loading={moveableAssets.loader}
            onFirstClick={() => {
              setPagination({
                ...pagination,
                pageNumber: 1,
                nextOrPrev: 'first',
              });
            }}
            onPrevClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber - 1,
                nextOrPrev: 'prev',
              });
            }}
            onNextClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber + 1,
                nextOrPrev: 'next',
              });
            }}
            isNext={moveableAssets.isNextPresent}
            pageNumber={pagination.pageNumber}
          />
          <Select
            className={classes.selectWithoutBorder}
            onChange={(resultPerPage) => {
              setPagination({ ...pagination, resultPerPage });
            }}
            value={pagination.resultPerPage}
            style={{ marginLeft: 16 }}
          >
            <Select.Option value={100}>{t('100/page')}</Select.Option>
            <Select.Option value={50}>{t('50/page')}</Select.Option>
            <Select.Option value={20}>{t('20/page')}</Select.Option>
            <Select.Option value={10}>{t('10/page')}</Select.Option>
          </Select>
          <Tooltip title={t('Refresh')}>
            <Button
              onClick={fetchViewDetailsAndCount}
              className={classes.refreshButton}
              style={{ marginLeft: 16 }}
            >
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </Row>
    );
  };

  const renderViewTable = () => (
    <Table
      rowKey={(record: MoveableAssetsRecord) => record['id']}
      className={classes.table}
      dataSource={moveableAssets.page_data || []}
      columns={generateColumnsToShow()}
      loading={moveableAssets.loader}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );

  const renderNavbar = () => <Navbar title={t('Moveable Assets')} />;

  return (
    <div className={classes.mainDiv}>
      {state.isMoveableAssetModalVisible && (
        <AddMoveableAssetModal
          isVisible={state.isMoveableAssetModalVisible}
          handleModalClose={() => handleCloseModal('isMoveableAssetModalVisible')}
          reloadView={fetchViewDetailsAndCount}
        />
      )}
      {renderNavbar()}
      <div className={classes.tabsContainer}>
        {renderFilters()}
        {renderViewTable()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { moveable_asset_column_list, vendor_config } = state.masterData;
  return {
    columnList: moveable_asset_column_list,
    vendorConfig: vendor_config,
    moveableAssets: state.moveableAssets,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setPagination: (data) =>
      dispatch({
        type: SET_MOVEABLE_ASSETS_PAGINATION,
        data,
      }),
    getMoveableAssets: (data) => dispatch(getMoveableAssets(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: MoveableAssetsStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(MoveableAssets);
